// ── Trademark Safety — Offline outbox + connectivity UI ──────────────────────
// Lets foremen complete & submit safety forms with no signal on-site. The PDF is
// generated locally (works offline), stored in an IndexedDB "outbox", and sent
// automatically the moment connectivity returns.
//
// Scope: single-shot PDF+email forms with NO structured dashboard sync, so a
// replay is just: upload PDF → log submission row → send email. Production
// Report and Weekly Timesheet are intentionally excluded (they expand into
// per-day dashboard rows and need a connection).
//
// Loaded before app.js; app.js calls saveSubmissionOffline()/showOfflineSavedScreen()
// from handleSubmit, and auth.js calls flushOutbox() after a successful login.

const OFFLINE_FORMS = ['Daily Tailgate', 'Weekly Toolbox Talk'];

// ═══════════════════════════════════════════════════════════════════════════════
//  IndexedDB outbox
// ═══════════════════════════════════════════════════════════════════════════════
const _OFFLINE_DB    = 'tm-offline';
const _OFFLINE_STORE = 'outbox';

function _openOutbox() {
  return new Promise((resolve, reject) => {
    let req;
    try { req = indexedDB.open(_OFFLINE_DB, 1); }
    catch (e) { reject(e); return; }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(_OFFLINE_STORE)) {
        db.createObjectStore(_OFFLINE_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

function _outboxAdd(record) {
  return _openOutbox().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(_OFFLINE_STORE, 'readwrite');
    const r  = tx.objectStore(_OFFLINE_STORE).add(record);
    r.onsuccess = () => resolve(r.result);
    r.onerror   = () => reject(r.error);
  }));
}
function _outboxAll() {
  return _openOutbox().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(_OFFLINE_STORE, 'readonly');
    const r  = tx.objectStore(_OFFLINE_STORE).getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror   = () => reject(r.error);
  }));
}
function _outboxDelete(id) {
  return _openOutbox().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(_OFFLINE_STORE, 'readwrite');
    const r  = tx.objectStore(_OFFLINE_STORE).delete(id);
    r.onsuccess = () => resolve();
    r.onerror   = () => reject(r.error);
  }));
}
async function outboxCount() {
  try { return (await _outboxAll()).length; } catch { return 0; }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Save a submission for later (called from app.js handleSubmit)
// ═══════════════════════════════════════════════════════════════════════════════
async function saveSubmissionOffline(s, pdfBuffer, pdfFilename) {
  // generatePDF returns an ArrayBuffer; normalise just in case a typed array slips in.
  let buf = pdfBuffer;
  if (pdfBuffer && pdfBuffer.buffer instanceof ArrayBuffer && !(pdfBuffer instanceof ArrayBuffer)) {
    buf = pdfBuffer.buffer.slice(pdfBuffer.byteOffset, pdfBuffer.byteOffset + pdfBuffer.byteLength);
  }
  // Honour the admin "submit on behalf of" picker if set. When an admin queues
  // a form for another foreman, the queued record carries that foreman's id +
  // name so the eventual replay writes to the correct submissions row.
  const onBehalfId   = (s.onBehalfOfForemanId   || null);
  const onBehalfName = (s.onBehalfOfForemanName || '');
  const effId   = onBehalfId   || ((window.currentUser && window.currentUser.id) || null);
  const effName = onBehalfName || ((window.currentProfile && window.currentProfile.full_name) || s.foremanName || '');

  const record = {
    queuedAt:       Date.now(),
    pdf:            buf,
    pdfFilename:    pdfFilename,
    submissionType: s.submissionType,
    project:        s.project,
    foremanName:    s.foremanName,
    date:           s.date,
    workersOnSite:  s.workersOnSite || '',
    sendToSafety:   !!s.sendToSafety,
    csoContact:     s._csoContact || null,
    userId:         effId,
    userName:       effName,
  };
  await _outboxAdd(record);
  _renderOfflineUI();
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Replay one queued submission: upload PDF → log row → send email
//  The email send (submitForm) MUST succeed for the record to be cleared; the
//  storage upload and submissions insert are best-effort (mirrors the online flow).
// ═══════════════════════════════════════════════════════════════════════════════
async function _replayRecord(rec) {
  const pdfBuffer = rec.pdf;

  // 1. Upload PDF to Storage (best-effort). upsert:true so a retry can't 409.
  let pdfStorageUrl = null, pdfStoragePath = null;
  try {
    if (rec.userId) {
      const fileName = `${rec.userId}/${rec.pdfFilename}`;
      pdfStoragePath = fileName;
      const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
      const { error: upErr } = await sbClient.storage
        .from('form-submissions')
        .upload(fileName, blob, { contentType: 'application/pdf', upsert: true });
      if (!upErr) {
        const { data } = await sbClient.storage
          .from('form-submissions')
          .createSignedUrl(fileName, 60 * 60 * 24 * 365);
        pdfStorageUrl = data?.signedUrl || null;
      }
    }
  } catch (e) { console.warn('[offline] storage upload failed on replay:', e); }

  // 2. Log submission row (best-effort).
  try {
    if (rec.userId) {
      await sbClient.from('submissions').insert({
        foreman_id:      rec.userId,
        foreman_name:    rec.userName,
        project_name:    rec.project,
        submission_type: rec.submissionType,
        pdf_url:         pdfStorageUrl,
        pdf_path:        pdfStoragePath,
      });
    }
  } catch (e) { console.warn('[offline] submissions insert failed on replay:', e); }

  // 3. Send the email — throws on failure, which keeps the record queued.
  const stateLike = {
    foremanName:    rec.userName || rec.foremanName,
    project:        rec.project,
    submissionType: rec.submissionType,
    date:           rec.date,
    workersOnSite:  rec.workersOnSite,
    sendToSafety:   rec.sendToSafety,
    _csoContact:    rec.csoContact,
  };
  await submitForm(stateLike, pdfBuffer, []);
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Flush the outbox (on reconnect + after login). Guarded so it only runs once
//  at a time and only when we have both a connection and an authenticated user
//  (writes need the session for RLS).
// ═══════════════════════════════════════════════════════════════════════════════
let _flushing = false;
async function flushOutbox(opts = {}) {
  if (_flushing) return;
  if (!navigator.onLine) return;
  if (!window.currentUser) return;

  let items;
  try { items = await _outboxAll(); } catch { return; }
  if (!items.length) { _renderOfflineUI(); return; }

  _flushing = true;
  let sent = 0, failed = 0;
  if (opts.toast !== false) {
    _toast(`Sending ${items.length} saved form${items.length > 1 ? 's' : ''}…`, 'info');
  }
  try {
    for (const rec of items) {
      try {
        await _replayRecord(rec);
        await _outboxDelete(rec.id);
        sent++;
      } catch (e) {
        console.warn('[offline] replay failed, will retry later:', e);
        failed++;
        if (!navigator.onLine) break;   // connection dropped mid-flush — stop
      }
    }
  } finally {
    _flushing = false;
    _renderOfflineUI();
  }
  if (sent)  _toast(`${sent} saved form${sent > 1 ? 's' : ''} sent`, 'success');
  if (failed && navigator.onLine) _toast(`${failed} form${failed > 1 ? 's' : ''} still pending — will retry`, 'warning');
}

// ═══════════════════════════════════════════════════════════════════════════════
//  UI: offline banner + pending-outbox pill + header dot
// ═══════════════════════════════════════════════════════════════════════════════
function _toast(msg, type) {
  try { if (typeof showToast === 'function') showToast(msg, type); } catch {}
}

function _ensureOfflineEls() {
  let banner = document.getElementById('offlineBanner');
  if (!banner) {
    banner = document.createElement('div');
    banner.id = 'offlineBanner';
    banner.className = 'offline-banner';
    banner.hidden = true;
    document.body.appendChild(banner);
  }
  let pill = document.getElementById('outboxPill');
  if (!pill) {
    pill = document.createElement('button');
    pill.id = 'outboxPill';
    pill.type = 'button';
    pill.className = 'outbox-pill';
    pill.hidden = true;
    pill.onclick = () => flushOutbox();
    document.body.appendChild(pill);
  }
  return { banner, pill };
}

async function _renderOfflineUI() {
  const { banner, pill } = _ensureOfflineEls();
  const online = navigator.onLine !== false;
  const count  = await outboxCount();

  banner.hidden = online;
  if (!online) {
    banner.innerHTML =
      '<span class="offline-banner-dot"></span> Offline — forms are saved on this device and will send when you reconnect.';
  }
  // Push page content down so the fixed banner never hides the header.
  if (document.body) document.body.classList.toggle('tm-offline', !online);

  pill.hidden = count === 0;
  if (count > 0) {
    pill.textContent = online
      ? `Sending ${count} saved form${count > 1 ? 's' : ''}…`
      : `${count} form${count > 1 ? 's' : ''} waiting to send`;
    pill.disabled = online;   // online ⇒ auto-sending; offline ⇒ informational
  }

  const dot = document.getElementById('backendStatus');
  if (dot) {
    dot.classList.toggle('offline', !online);
    dot.title = online ? 'Online' : 'Offline — forms saved locally';
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  "Saved offline" success screen (mirrors app.js showSuccessScreen)
// ═══════════════════════════════════════════════════════════════════════════════
function showOfflineSavedScreen(pdfFilename) {
  const panel = document.getElementById('step4');
  if (!panel) return;
  panel.innerHTML = `
    <div class="success-screen">
      <div class="success-icon"></div>
      <h2 class="success-title">Saved on this device</h2>
      <p class="success-msg">No connection right now — your form is safe and will send automatically the moment you're back online.</p>
      ${pdfFilename ? `<div class="success-saved-path"><span class="success-saved-icon">⬇</span><span class="success-saved-text">${pdfFilename} downloaded</span></div>` : ''}
      <button class="btn btn-next" onclick="resetApp()" style="margin-top:24px;width:100%">+ New Submission</button>
    </div>`;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Boot
// ═══════════════════════════════════════════════════════════════════════════════
function _registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  // ── Auto-update ────────────────────────────────────────────────────────────
  // A PWA that stays open on a phone keeps running the JS it booted with, so a
  // deploy never reaches it until the app is fully closed + reopened. That bit
  // us hard: foremen submitted timesheets whose PDF was built by a stale
  // pdf-generator bundle (no SICK marker) even though the fix was already live.
  //
  // Fix: when a freshly-deployed service worker installs, activate it and reload
  // the page so the newest bundles load — but ONLY at a safe moment. The reload
  // is gated on `state.submissionType` being empty, i.e. the foreman is sitting
  // on the form picker with nothing half-filled, so we can never wipe a form in
  // progress (and never interrupt the post-submit success screen).
  let _reloaded = false;
  let _updatePending = false;
  // If the page was already controlled by a service worker when it loaded, a
  // later controllerchange means a NEW worker took over (a fresh deploy). If it
  // wasn't controlled, the first controllerchange is just the initial install —
  // not an update, so we must not reload on it.
  const _hadControllerAtLoad = !!navigator.serviceWorker.controller;

  const _onPicker = () => {
    try { return typeof state === 'undefined' || !state.submissionType; }
    catch { return true; }
  };
  const _maybeReload = () => {
    if (_updatePending && !_reloaded && _onPicker()) {
      _reloaded = true;
      window.location.reload();
    }
  };

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!_hadControllerAtLoad) return;   // initial install, not a new deploy
    _updatePending = true;
    _maybeReload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('sw.js').then(reg => {
      const checkForUpdate = () => { reg.update().catch(() => {}); };

      // Re-check for a new deploy whenever the foreman brings the app forward,
      // and take the chance to land a pending update if they're idle on the picker.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') { checkForUpdate(); _maybeReload(); }
      });
      window.addEventListener('focus', _maybeReload);

      // A new worker finished installing while the old one still controls the
      // page → tell it to take over immediately (sw.js listens for SKIP_WAITING).
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            nw.postMessage('SKIP_WAITING');
          }
        });
      });

      // Safety net: if an update is waiting and the foreman lingers on the
      // picker, land it within ~30s instead of waiting for the next app open.
      setInterval(_maybeReload, 30000);
    }).catch(e => console.warn('[sw] register failed:', e));
  });
}

function initOffline() {
  _renderOfflineUI();
  window.addEventListener('online',  () => { _renderOfflineUI(); flushOutbox(); });
  window.addEventListener('offline', () => { _renderOfflineUI(); });
}

_registerServiceWorker();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOffline);
} else {
  initOffline();
}
